import { apiClient, apiClientPaginated } from '@/lib/api-client';
import type {
  User,
  CreateUserPayload,
  UpdateUserPayload,
  UserLocation,
  UserPerformance,
  UserShift,
  UserSession,
} from '@/types/user';

export const userService = {
  list(params: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    is_active?: string;
    location_id?: string;
    tenant_id?: string;
    mfa_enabled?: 'true' | 'false';
    mfa_required?: 'true' | 'false';
  }) {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.search) query.set('search', params.search);
    if (params.role) query.set('role', params.role);
    if (params.is_active) query.set('is_active', params.is_active);
    if (params.location_id) query.set('location_id', params.location_id);
    if (params.tenant_id) query.set('tenant_id', params.tenant_id);
    if (params.mfa_enabled) query.set('mfa_enabled', params.mfa_enabled);
    if (params.mfa_required) query.set('mfa_required', params.mfa_required);
    const qs = query.toString();
    return apiClientPaginated<User[]>(`/users${qs ? `?${qs}` : ''}`);
  },

  getById(id: string) {
    return apiClient<User>(`/users/${id}`);
  },

  create(payload: CreateUserPayload) {
    return apiClient<User>('/users', { method: 'POST', body: payload });
  },

  update(id: string, payload: UpdateUserPayload) {
    return apiClient<User>(`/users/${id}`, { method: 'PATCH', body: payload });
  },

  deactivate(id: string) {
    return apiClient<User>(`/users/${id}`, {
      method: 'PATCH',
      body: { is_active: false },
    });
  },

  reactivate(id: string) {
    return apiClient<User>(`/users/${id}`, {
      method: 'PATCH',
      body: { is_active: true },
    });
  },

  assignLocations(id: string, locationIds: string[]) {
    return apiClient<UserLocation[]>(`/users/${id}/locations`, {
      method: 'PUT',
      body: { location_ids: locationIds },
    });
  },

  getLocations(id: string) {
    return apiClient<UserLocation[]>(`/users/${id}/locations`);
  },

  sendInvite(id: string) {
    return apiClient<{ message: string }>(`/users/${id}/invite`, { method: 'POST' });
  },

  getShifts(userId: string, params?: { page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    return apiClientPaginated<UserShift[]>(`/users/${userId}/shifts${qs ? `?${qs}` : ''}`);
  },

  getSessions(userId: string) {
    return apiClient<UserSession[]>(`/auth/sessions?user_id=${userId}`);
  },

  getPerformance(userId: string) {
    return apiClient<UserPerformance>(`/users/${userId}/performance`);
  },
};
